import React, {Component} from 'react';
import {useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';

import ErrorContent from '@/pages/Errors/components/ErrorContent';
import {ROUTES} from '@/utils/constants/routesConstants';
import {logger} from '@/helpers/logger';

/**
 * Class-based error boundary that catches render errors via componentDidCatch
 * and also listens for global unhandled errors/rejections.
 */
class ErrorBoundaryClass extends Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false};
    this.handleError = this.handleError.bind(this);
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this);
    this.handleGoHome = this.handleGoHome.bind(this);
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error, errorInfo) {
    logger.error('ErrorBoundary caught a render error:', error, errorInfo);
  }

  componentDidMount() {
    globalThis.addEventListener('error', this.handleError);
    globalThis.addEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection
    );
  }

  componentWillUnmount() {
    globalThis.removeEventListener('error', this.handleError);
    globalThis.removeEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection
    );
  }

  handleError(errorEvent) {
    logger.error('ErrorBoundary caught an error:', errorEvent.error);
    this.setState({hasError: true});
  }

  handleUnhandledRejection(event) {
    logger.error(
      'ErrorBoundary caught an unhandled promise rejection:',
      event.reason
    );
    this.setState({hasError: true});
  }

  handleGoHome() {
    const {onGoHome, navigate} = this.props;
    if (onGoHome) {
      onGoHome();
    } else if (navigate) {
      navigate(ROUTES.HOME);
      globalThis.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContent errorType='SERVER_ERROR' onGoHome={this.handleGoHome} />
      );
    }
    return this.props.children;
  }
}

ErrorBoundaryClass.propTypes = {
  children: PropTypes.node.isRequired,
  onGoHome: PropTypes.func,
  navigate: PropTypes.func
};

/**
 * Functional wrapper that injects router's navigate into the class-based boundary.
 */
const ErrorBoundary = (props) => {
  const navigate = useNavigate();
  return <ErrorBoundaryClass {...props} navigate={navigate} />;
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onGoHome: PropTypes.func
};

export default ErrorBoundary;
